/**
 * Die Chronik eines Angebots: Statuswechsel, Mails, Änderungen und
 * Rückmeldungen in einem Strom, neueste zuerst.
 *
 * Bewusst an einer Stelle: Die Angebotsmaske zeigt sie vollständig, „Meine
 * Aufgaben" zeigt die letzten Einträge. Zwei Umsetzungen desselben Verlaufs
 * würden auseinanderlaufen – und dann stünde in der einen Ansicht ein Eintrag,
 * den die andere nicht kennt.
 */

import { dat } from './format';
import type { Angebot } from '../typen';

export type ChronikArt = 'status' | 'mail' | 'rueckmeldung' | 'aenderung';

export interface ChronikEintrag {
  id: string;
  art: ChronikArt;
  /** Sonderfall: die Anlage. Sie ist ein Statuswechsel, liest sich aber nicht so. */
  anlage: boolean;
  sortier: string;
  titel: string;
  unter: string;
  notiz: string;
  zeitText: string;
  /** Kurzform für die Zeitachse in „Meine Aufgaben": „31. Aug." */
  datumKurz: string;
  /** Wer den Eintrag verursacht hat. */
  person: string;
  /** Nur bei Mails gesetzt – führt zum Wortlaut. */
  mailId?: number;
}

const MONATE = [
  'Jän.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.',
  'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.',
];

/** „2026-08-31" wird zu „31. Aug." – in einer Zeile zählt jedes Zeichen. */
export function datumKurz(iso: string): string {
  const [, monat, tag] = iso.split('-');
  const m = MONATE[Number(monat) - 1];
  return m ? `${Number(tag)}. ${m}` : iso;
}

export function chronikBauen(a: Angebot): ChronikEintrag[] {
  return [
    ...a.verlauf.map((v, i) => {
      const anlage = v.status === 'START' && i === 0;
      return {
        id: `s${i}`,
        art: 'status' as ChronikArt,
        anlage,
        sortier: `${v.datum} ${v.zeit || '00:00'}`,
        titel: anlage ? 'Angebot angelegt' : `Status → ${v.status}`,
        unter: anlage ? `durch ${v.user}` : `in KORA durch ${v.user}`,
        notiz: anlage ? '' : v.notiz,
        zeitText: `${v.wochentag}, ${v.datumText}${v.zeit ? ` · ${v.zeit}` : ''}`,
        datumKurz: datumKurz(v.datum),
        person: v.user,
      };
    }),
    ...a.erinnerungen.map((e) => ({
      id: `m${e.id}`,
      art: 'mail' as ChronikArt,
      mailId: e.id,
      anlage: false,
      sortier: `${e.datum} ${e.zeit}`,
      titel:
        e.art === 'auto'
          ? `${e.stufe}. Erinnerung automatisch versendet`
          : `${e.stufe}. Erinnerung ${e.artText}`,
      unter:
        `im Namen von ${e.user}` +
        (e.trockenlauf
          ? ' · Testbetrieb, nur als Entwurf abgelegt'
          : a.nachfass.antwortAm
            ? ` · Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt`
            : ' · keine Antwort erkannt'),
      notiz: '',
      zeitText: `${e.wochentag}, ${e.datumText} · ${e.zeit}`,
      datumKurz: datumKurz(e.datum),
      person: e.user,
    })),
    ...a.aenderungen.map((x) => ({
      id: `a${x.id}`,
      art: 'aenderung' as ChronikArt,
      anlage: false,
      sortier: `${x.datum} ${x.zeit}`,
      titel: `${x.feld} geändert`,
      unter: `in KORA durch ${x.user}`,
      // Der alte Wert gehört dazu: Ohne ihn sagt der Eintrag nicht, was geschah.
      notiz: `${x.vorher || 'nicht gesetzt'}  →  ${x.nachher || 'entfernt'}`,
      zeitText: `${x.wochentag}, ${x.datumText} · ${x.zeit}`,
      datumKurz: datumKurz(x.datum),
      person: x.user,
    })),
    ...a.anrufe.map((c) => ({
      id: `r${c.id}`,
      art: 'rueckmeldung' as ChronikArt,
      anlage: false,
      sortier: `${c.datum} ${c.zeit}`,
      titel:
        'Rückmeldung · ' +
        (c.wiedervorlage ? `Wiedervorlage für ${dat(c.wiedervorlage)} vereinbart` : c.ergebnisLabel),
      unter:
        `${c.kanalLabel} · erfasst von ${c.user}` +
        (c.statusGesetzt
          ? ` · Status → ${c.statusGesetzt}`
          : c.wiedervorlage
            ? ' · Nachfassen ruhte seither'
            : ''),
      notiz: c.notiz,
      zeitText: `${c.wochentag}, ${c.datumText} · ${c.zeit}`,
      datumKurz: datumKurz(c.datum),
      person: c.user,
    })),
  ].sort((x, y) => (x.sortier < y.sortier ? 1 : -1));
}